import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api, type MencionesResumen, type MencionOut } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaMenciones } from "@/components/ayuda/vistas/menciones";
import { useToast } from "@/ui/Toast";

/**
 * "Esperan mi respuesta" — bandeja de menciones del usuario. Un compañero te
 * @-mencionó en la bitácora de un caso. Espeja el patrón de Alertas:
 *   - Click en la mención → te lleva al caso y la deja LEÍDA (gris, NO se borra).
 *   - El badge "Esperan" cuenta solo las SIN LEER.
 *   - "Resuelta" la saca de la lista; "Descartar" (autor/admin) la quita por error.
 *   - Al escribir una nota en el caso, tu mención ahí se resuelve sola (RESPONDIO).
 * Excluye casos cerrados (el backend ya filtra).
 */
export default function Menciones() {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const nav = useNavigate();
  const [items, setItems] = useState<MencionOut[]>([]);
  const [loading, setLoading] = useState(true);

  const sinLeer = items.filter((m) => !m.leida_at).length;

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<MencionesResumen>("/api/menciones");
      setItems(r.items);
      window.dispatchEvent(new Event("menciones:changed"));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar las menciones", msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atender: te lleva al caso (a su bitácora) y deja la mención LEÍDA. No se
  // borra — queda gris hasta que la resuelvas (o respondas en el caso). Es el
  // flujo principal: llegas al caso y deja de gritar en el badge.
  async function atender(m: MencionOut) {
    if (!m.leida_at) {
      try {
        await api.post(`/api/menciones/${m.id}/leer`);
        window.dispatchEvent(new Event("menciones:changed"));
      } catch {
        /* si falla el marcado, no bloqueamos la navegación al caso */
      }
    }
    if (m.caso_folio) {
      nav(`/casos/${m.caso_folio}`);
    } else {
      await load();
    }
  }

  // Resuelta: la das por cerrada y desaparece de la lista (la hace el
  // mencionado o un admin). El auto-resuelve al responder usa el mismo estado.
  async function resolver(m: MencionOut) {
    try {
      await api.post(`/api/menciones/${m.id}/atender`);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      window.dispatchEvent(new Event("menciones:changed"));
      toast.success("Mención resuelta");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude resolver", msg);
    }
  }

  // Descartar: la quita sin responder (solo el autor de la mención o admin).
  async function descartar(m: MencionOut) {
    try {
      await api.post(`/api/menciones/${m.id}/descartar`);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      window.dispatchEvent(new Event("menciones:changed"));
      toast.success("Mención descartada");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude descartar", msg);
    }
  }

  function puedeDescartar(m: MencionOut): boolean {
    return m.autor_id === me?.id || me?.rol === "admin";
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-xl font-semibold">Esperan mi respuesta</h1>
            <p className="text-sm text-zinc-400">{sinLeer} sin leer</p>
          </div>
          <AyudaVista titulo="Esperan mi respuesta" {...ayudaMenciones()} />
        </div>
        <button onClick={() => void load()} className="btn-ghost w-full sm:w-auto">
          ↻ Refrescar
        </button>
      </div>

      <div className="card">
        {loading ? (
          <p className="text-sm text-zinc-500">Cargando...</p>
        ) : items.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-zinc-500">Nada pendiente — todo al día ✓</p>
            <p className="text-xs text-zinc-600 mt-1">
              Cuando un compañero te mencione con @ en la bitácora de un caso,
              aparecerá aquí.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-ink-700">
            {items.map((m) => (
              <li
                key={m.id}
                className={`py-3 transition ${m.leida_at ? "opacity-60" : ""}`}
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <button
                    type="button"
                    onClick={() => void atender(m)}
                    className="flex-1 min-w-0 text-left rounded -mx-1 px-1 py-0.5 hover:bg-ink-800/50 transition cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`h-2 w-2 rounded-full flex-shrink-0 ${
                          m.leida_at ? "bg-zinc-600" : "bg-accent-500"
                        }`}
                      />
                      {m.caso_folio && (
                        <span className="font-mono text-accent-400 text-sm">
                          {m.caso_folio}
                        </span>
                      )}
                      {m.leida_at ? (
                        <span className="text-[10px] uppercase tracking-wide text-zinc-600">
                          Leída
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-500/30 bg-accent-500/10 text-accent-300">
                          te mencionó
                        </span>
                      )}
                      <span className="text-accent-500/80 text-xs">· abrir caso →</span>
                    </div>
                    <p className="text-sm text-zinc-200 mt-1">
                      <span className="text-zinc-400">{m.autor_nombre || "Alguien"}</span>{" "}
                      te mencionó
                    </p>
                    {m.texto && (
                      <p className="text-xs text-zinc-400 mt-0.5 line-clamp-3 whitespace-pre-wrap break-words">
                        {m.texto}
                      </p>
                    )}
                    <p className="text-xs text-zinc-600 mt-1">
                      {new Date(m.created_at).toLocaleString()}
                    </p>
                  </button>
                  <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
                    <button
                      onClick={() => void atender(m)}
                      className="btn-primary text-sm w-full sm:w-auto"
                    >
                      Atender →
                    </button>
                    <button
                      onClick={() => void resolver(m)}
                      className="btn-ghost text-sm w-full sm:w-auto"
                    >
                      Resuelta
                    </button>
                    {puedeDescartar(m) && (
                      <button
                        onClick={() => void descartar(m)}
                        className="btn-ghost text-sm text-red-400 hover:text-red-300 w-full sm:w-auto"
                      >
                        Descartar
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
